import Button from "@/components/button/Button"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import { SvgIcon } from "@/components/icon"
import DividerWithHex from "@/components/modal-dark/components/divider-with-hex/DividerWithHex"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { Col, Row } from "antd"
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint"
import { FC } from "react"
import "./DeleteConfirmation.less"

const { Title, Paragraph } = WebsiteTypography

export type DeleteConfirmationContent = {
  /** What is about to be deleted, e.g. "application" */
  subject: string
  /** Replaces the default "Are you sure…" text when what goes with the delete needs spelling out */
  message?: string
}

/**
 * The only confirmation before deleting a project, an application or an approval item, opened by `useConfirmDelete`
 * (via `useDeleteProject`, `useDeleteApplication`, `useDeleteApprovalItem`). "Delete" runs the delete handed over
 * (`onSubmit`); "Cancel" (or Esc) leaves everything as it was.
 */
const DeleteConfirmation: FC = () => {
  const { xxl, xl, lg, md } = useBreakpoint()
  const isMobileScr = !(xxl || xl || lg || md)

  const { setIsModalDarkOpen, onSubmit, setOnSubmit, modalDarkContent } = useModalDarkContext()
  const content = modalDarkContent as DeleteConfirmationContent | undefined
  const subject = content?.subject || "item"
  const message =
    content?.message || `Are you sure you want to delete this ${subject}? Once deleted, it can’t be restored.`

  const handleDelete = () => {
    onSubmit?.()
    setOnSubmit(undefined)
    setIsModalDarkOpen(false)
  }

  const handleCancel = () => {
    setOnSubmit(undefined)
    setIsModalDarkOpen(false)
  }

  return (
    <Row justify={"center"} className={"delete-confirmation-wrapper"} style={{ paddingBottom: 24 }}>
      <Col span={20} md={20} sm={24} xs={24}>
        <Row justify={"center"}>
          <div className={"delete-confirmation-hex-wrapper"}>
            <SvgIcon type={"exclamation-triangle"} />
          </div>
        </Row>

        <Title
          level={isMobileScr ? 5 : 4}
          centered
          className={"section-title"}
          style={{ paddingTop: 12, paddingBottom: 16 }}
        >
          <span className={"bold"}>{"This action is irreversible"}</span>
        </Title>

        <Paragraph centered size={isMobileScr ? "sm" : "md"} color={"website-secondary"} weight={"w300"}>
          {message}
        </Paragraph>

        <DividerWithHex />
      </Col>

      <Col span={isMobileScr ? 24 : 20}>
        <Row justify={"center"} style={{ paddingTop: 8, width: "100%" }} gutter={[16, 16]}>
          <Col span={isMobileScr ? 24 : 12}>
            <Button btnType={"ghost"} text={"Cancel"} onClick={handleCancel} block textColor={"white"} size={"large"} />
          </Col>

          <Col span={isMobileScr ? 24 : 12}>
            <Button
              btnType={"primary"}
              className={"delete-confirmation-delete-btn"}
              text={"Delete"}
              onClick={handleDelete}
              block
              size={"large"}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default DeleteConfirmation
